import { oc } from '@orpc/contract';
import { z } from 'zod';
import { Id } from './common.js';
/** Локализованное название: { ru: '…', en?: '…' }. */
export const LocalizedName = z.object({
    ru: z.string().min(1).max(200),
    en: z.string().max(200).optional(),
});
export const Code = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'code_format');
/**
 * Закрытый список типов данных полей (docs/PLAN.md, раздел 6.2).
 * Только новый тип данных требует кода: виджет в UI и ветку валидации в domain.
 */
export const DataType = z.enum([
    'text',
    'longtext',
    'integer',
    'decimal',
    'boolean',
    'date',
    'partial_date',
    'year_range',
    'enum',
    'multi_enum',
    'ref',
    'money',
    'grade',
    'list',
    'url',
]);
/** Где поле может заполняться: запись каталога, разновидность, экземпляр в коллекции. */
export const FieldLevel = z.enum(['catalog', 'variant', 'specimen']);
/** Справочники, на которые может ссылаться поле `ref`. */
export const RefKind = z.enum([
    'issuers',
    'periods',
    'currency_units',
    'mints',
    'printers',
    'grading_services',
    'numbering_systems',
    'statuses',
    'collections',
    'storage_locations',
    'variants',
]);
/** Элементы поля `list`. */
export const ListItemType = z.enum(['text', 'url', 'catalog_number']);
export const FieldSection = z.enum(['main', 'physical', 'design', 'paper', 'specimen', 'notes']);
export const FieldStorage = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('column'), column: z.string() }),
    z.object({ kind: z.literal('attributes') }),
    z.object({ kind: z.literal('relation'), relation: z.string() }),
]);
export const FieldValidation = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    maxLength: z.number().int().positive().optional(),
    maxItems: z.number().int().positive().optional(),
    pattern: z.string().optional(),
});
export const FieldUi = z.object({
    /** Переопределение виджета, выбранного по dataType. */
    widget: z.string().optional(),
    section: FieldSection,
    showInTable: z.boolean(),
    filterable: z.boolean(),
    sortable: z.boolean(),
});
export const FieldDefinition = z.object({
    code: Code,
    name: LocalizedName,
    dataType: DataType,
    levels: z.array(FieldLevel).min(1),
    storage: FieldStorage,
    /** Для enum/multi_enum — набор значений. */
    enumSet: Code.nullable(),
    /** Для ref — справочник. */
    refKind: RefKind.nullable(),
    /** Для list — тип элементов. */
    listOf: ListItemType.nullable(),
    /** Единица измерения для отображения (г, мм). */
    unit: z.string().nullable(),
    validation: FieldValidation,
    ui: FieldUi,
});
export const ItemTypeField = z.object({
    field: Code,
    required: z.boolean(),
    section: FieldSection,
    sortKey: z.string(),
});
export const ItemType = z.object({
    id: Id,
    code: Code,
    name: LocalizedName,
    icon: z.string().nullable(),
    /** Шаблон названия: {field_code} подставляются значениями. */
    titleTemplate: z.string(),
    defaultGradeScale: Code.nullable(),
    sortKey: z.string(),
    fields: z.array(ItemTypeField),
    /** false — деактивировано: не предлагается для новых значений, существующие данные показываются. */
    active: z.boolean().optional(),
});
export const EnumOption = z.object({
    code: Code,
    name: LocalizedName,
    sortKey: z.string(),
    /** false — деактивировано: не предлагается для новых значений, существующие данные показываются. */
    active: z.boolean().optional(),
});
export const Status = z.object({
    id: Id,
    code: Code,
    name: LocalizedName,
    color: z.string(),
    /** Экземпляр физически принадлежит владельцу (учитывается в стоимости коллекции). */
    isOwned: z.boolean(),
    sortKey: z.string(),
    /** false — деактивировано: не предлагается для новых значений, существующие данные показываются. */
    active: z.boolean().optional(),
});
export const GradeValue = z.object({
    code: z.string().min(1).max(32),
    name: LocalizedName,
    /** Общая ось 0–100 для сравнения значений разных шкал. */
    rank: z.number().int().min(0).max(100),
    /** false — деактивировано: не предлагается для новых значений, существующие данные показываются. */
    active: z.boolean().optional(),
});
export const GradeScale = z.object({
    code: Code,
    name: LocalizedName,
    values: z.array(GradeValue),
    /** false — деактивировано: не предлагается для новых значений, существующие данные показываются. */
    active: z.boolean().optional(),
});
export const Currency = z.object({
    code: z.string().regex(/^[A-Z]{3}$/),
    name: LocalizedName,
    minorUnits: z.number().int().min(0).max(4),
});
/**
 * Все метаданные одним ответом: из них строятся формы, таблицы, фильтры и валидация.
 * version — хеш содержимого: клиент (и будущий desktop) сравнивает его, чтобы понять, что изменилось.
 */
export const Meta = z.object({
    version: z.string(),
    itemTypes: z.array(ItemType),
    fields: z.array(FieldDefinition),
    enums: z.record(z.string(), z.array(EnumOption)),
    statuses: z.array(Status),
    gradeScales: z.array(GradeScale),
    currencies: z.array(Currency),
});
export const metaContract = {
    get: oc.route({ method: 'GET', path: '/meta' }).output(Meta),
};
//# sourceMappingURL=meta.js.map